/**
 * S/R Auto — a faithful port of TradingView's "Auto Trendlines" algorithm
 * (tradingview.com/support/solutions/43000741165). Separate from S/R Trendlines /
 * S/R Pro / S/R TrendSpider; all of those are left untouched.
 *
 * The three ideas that make it clean (and that the simpler detectors lack):
 *   1. ATR-SIGNIFICANT pivots in two tiers — a pivot only counts if it protrudes
 *      by an ATR multiple, so noise wiggles never anchor a line:
 *        Large = 25/25 fractal protruding > 5 x ATR(14)
 *        Small =  5/5  fractal protruding > 2 x ATR(14)
 *   2. Lines STOP at breakout (N consecutive closes beyond the line) and their
 *      extension past the second anchor is capped at 2x the base length — so a
 *      short line can't shoot across the whole chart.
 *   3. Overlap resolution — when two lines' bases overlap > 30%, only the best
 *      survives (by touches -> length -> pivot size -> shallower slope), so no fans.
 *
 * Pure — `SrAutoOverlay` turns these into screen coordinates.
 */
import type { Bar } from "@/lib/chart/indicatorTypes";

export type SrAutoLine = {
  t1: number;
  p1: number;
  t2: number; // second anchor time
  p2: number;
  slope: number; // price per ms
  /** Time the drawn line ends — breakout bar, or the capped extension. */
  endT: number;
  type: "support" | "resistance";
  size: "small" | "large";
  touches: number;
  broken: boolean;
  /** Pivot bar times (for the Show Pivots option). */
  pivT: number[];
  pivP: number[];
};

export type SrAutoParams = {
  barsToBreakout?: unknown; // consecutive closes beyond the line = broken
  lineSize?: unknown; // "both" | "small" | "large"
  showPivots?: unknown;
  showLabels?: unknown;
  largePivot?: unknown; // large fractal half-window (25)
  largeAtr?: unknown; // large protrusion in ATR (5)
  smallPivot?: unknown; // small fractal half-window (5)
  smallAtr?: unknown; // small protrusion in ATR (2)
  atrLen?: unknown; // ATR period (14)
  touchPct?: unknown; // tolerance for a 3rd-pivot "touch"
  maxLines?: unknown;
  lookback?: unknown; // bars analysed
};

const num = (v: unknown, d: number) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};
const flag = (v: unknown, d: boolean) =>
  v === undefined || v === null ? d : !(v === false || v === 0 || v === "false" || v === "0" || v === "off");

/** Wilder ATR series (RMA of true range), index-aligned to `bars`. */
export function atrSeries(bars: Bar[], len: number): number[] {
  const n = bars.length;
  const atr = new Array<number>(n).fill(0);
  if (n < 2) return atr;
  const L = Math.max(1, len);
  const tr = new Array<number>(n).fill(0);
  for (let i = 1; i < n; i++) {
    const h = bars[i]!.high;
    const l = bars[i]!.low;
    const pc = bars[i - 1]!.close;
    tr[i] = Math.max(h - l, Math.abs(h - pc), Math.abs(l - pc));
  }
  let seed = 0;
  const m = Math.min(L, n - 1);
  for (let i = 1; i <= m; i++) seed += tr[i]!;
  let prev = m > 0 ? seed / m : 0;
  for (let i = 1; i < n; i++) {
    prev = i <= L ? prev : (prev * (L - 1) + tr[i]!) / L;
    atr[i] = prev;
  }
  return atr;
}

/** Significant fractal pivots: a ±L extreme that protrudes from its window by
 *  more than `mult` x ATR at that bar. */
export function sigPivots(
  bars: Bar[],
  atr: number[],
  L: number,
  mult: number,
  start: number,
): { hi: number[]; lo: number[] } {
  const n = bars.length;
  const hi: number[] = [];
  const lo: number[] = [];
  for (let i = Math.max(start, L); i < n - L; i++) {
    let isHi = true;
    let isLo = true;
    let lowest = Infinity;
    let highest = -Infinity;
    for (let k = i - L; k <= i + L; k++) {
      if (k === i) continue;
      if (bars[k]!.high >= bars[i]!.high) isHi = false;
      if (bars[k]!.low <= bars[i]!.low) isLo = false;
      if (bars[k]!.low < lowest) lowest = bars[k]!.low;
      if (bars[k]!.high > highest) highest = bars[k]!.high;
    }
    const a = atr[i] || 0;
    if (isHi && bars[i]!.high - lowest > mult * a) hi.push(i);
    if (isLo && highest - bars[i]!.low > mult * a) lo.push(i);
  }
  return { hi, lo };
}

export function computeSrAuto(bars: Bar[], p: SrAutoParams = {}): SrAutoLine[] {
  const n = Array.isArray(bars) ? bars.length : 0;
  const barsToBreakout = Math.max(1, Math.round(num(p.barsToBreakout, 3)));
  const lineSize = String(p.lineSize ?? "both");
  const wantLarge = lineSize !== "small";
  const wantSmall = lineSize !== "large";
  const largeL = Math.max(2, Math.round(num(p.largePivot, 25)));
  const largeMult = Math.max(0, num(p.largeAtr, 5));
  const smallL = Math.max(2, Math.round(num(p.smallPivot, 5)));
  const smallMult = Math.max(0, num(p.smallAtr, 2));
  const atrLen = Math.max(2, Math.round(num(p.atrLen, 14)));
  const tolPct = Math.max(0, num(p.touchPct, 0.5)) / 100;
  const maxLines = Math.max(1, Math.round(num(p.maxLines, 8)));
  const lookback = Math.max(2 * largeL + 2, Math.round(num(p.lookback, 5000)));
  if (n < 2 * smallL + 2) return [];

  const start = Math.max(0, n - lookback);
  const atr = atrSeries(bars, atrLen);
  const lastT = bars[n - 1]!.t;

  const tiers: { piv: { hi: number[]; lo: number[] }; size: "small" | "large" }[] = [];
  if (wantLarge) tiers.push({ piv: sigPivots(bars, atr, largeL, largeMult, start), size: "large" });
  if (wantSmall) tiers.push({ piv: sigPivots(bars, atr, smallL, smallMult, start), size: "small" });

  const out: SrAutoLine[] = [];

  for (const { piv, size } of tiers) {
    const build = (idxs: number[], type: "support" | "resistance") => {
      const priceOf = (i: number) => (type === "resistance" ? bars[i]!.high : bars[i]!.low);
      for (let a = 0; a < idxs.length; a++) {
        for (let b = a + 1; b < idxs.length; b++) {
          const i1 = idxs[a]!;
          const i2 = idxs[b]!;
          const t1 = bars[i1]!.t;
          const t2 = bars[i2]!.t;
          if (t2 === t1) continue;
          const y1 = priceOf(i1);
          const y2 = priceOf(i2);
          const slope = (y2 - y1) / (t2 - t1);
          const at = (t: number) => y1 + slope * (t - t1);
          // Validity + touches: no same-type pivot between the anchors may pierce
          // the line; count pivots within tolerance (the "3/3 touches").
          let valid = true;
          let touches = 0;
          const pivT: number[] = [];
          const pivP: number[] = [];
          for (const pj of idxs) {
            const ly = at(bars[pj]!.t);
            if (ly <= 0) continue;
            const dev = (priceOf(pj) - ly) / ly;
            if (Math.abs(dev) <= tolPct) {
              touches++;
              pivT.push(bars[pj]!.t);
              pivP.push(priceOf(pj));
            }
            if (pj > i1 && pj < i2) {
              if (type === "resistance" && priceOf(pj) - ly > tolPct * ly) valid = false;
              if (type === "support" && ly - priceOf(pj) > tolPct * ly) valid = false;
            }
          }
          if (!valid || touches < 2) continue;

          // Breakout: first bar after the 2nd anchor with `barsToBreakout`
          // consecutive closes beyond the line.
          let breakIdx = -1;
          let run = 0;
          for (let k = i2 + 1; k < n; k++) {
            const ly = at(bars[k]!.t);
            if (ly <= 0) continue;
            const beyond = type === "resistance" ? bars[k]!.close > ly : bars[k]!.close < ly;
            if (beyond) {
              run++;
              if (run >= barsToBreakout) {
                breakIdx = k;
                break;
              }
            } else run = 0;
          }

          // Extension capped at 2x the base length; stop at the breakout.
          const baseLen = t2 - t1;
          const maxEndT = t2 + 2 * baseLen;
          const endT = breakIdx >= 0 ? Math.min(bars[breakIdx]!.t, maxEndT) : maxEndT;
          out.push({
            t1,
            p1: y1,
            t2,
            p2: y2,
            slope,
            endT,
            type,
            size,
            touches,
            broken: breakIdx >= 0,
            pivT,
            pivP,
          });
        }
      }
    };
    build(piv.hi, "resistance");
    build(piv.lo, "support");
  }

  if (out.length === 0) return [];

  // Overlap resolution: when two same-type lines' bases overlap > 30%, keep the
  // better one (touches -> length -> size -> shallower slope). Greedy over a
  // strength-sorted list, so a fan collapses to its strongest member.
  const sizeRank = (s: "small" | "large") => (s === "large" ? 1 : 0);
  out.sort(
    (x, y) =>
      y.touches - x.touches ||
      y.endT - y.t1 - (x.endT - x.t1) ||
      sizeRank(y.size) - sizeRank(x.size) ||
      Math.abs(x.slope) - Math.abs(y.slope),
  );
  const overlaps = (a: SrAutoLine, c: SrAutoLine) => {
    if (a.type !== c.type) return false;
    const aLen = a.t2 - a.t1;
    const cLen = c.t2 - c.t1;
    const lo = Math.max(a.t1, c.t1);
    const hi = Math.min(a.t2, c.t2);
    const inter = Math.max(0, hi - lo);
    return inter > 0.3 * Math.min(aLen, cLen);
  };
  const kept: SrAutoLine[] = [];
  for (const ln of out) {
    if (kept.some((k) => overlaps(ln, k))) continue;
    kept.push(ln);
    if (kept.length >= maxLines * 2) break;
  }

  // Prefer lines still relevant near the last bar (unbroken, or broken recently),
  // then cap. Keeps the currently-active trendlines.
  kept.sort((x, y) => {
    if (x.broken !== y.broken) return x.broken ? 1 : -1;
    return y.endT - x.endT;
  });
  return kept.slice(0, maxLines);
}
